import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogIn, LogOut, IdCard, Ban, ShieldAlert, Info, KeyRound, Clock, ScrollText, CircleAlert } from "lucide-react";
import api from "../lib/api";
import { Reveal } from "../components/motion";

function Card({ icon: Icon, title, children, tone = "gold", testid }) {
  return (
    <Reveal>
      <section className="glass p-7 h-full" data-testid={testid}>
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 border flex items-center justify-center ${tone === "crimson" ? "border-red-500/40" : "border-gold/30"}`}>
            <Icon size={16} className={tone === "crimson" ? "text-red-400" : "text-gold"} />
          </div>
          <h2 className="font-display text-xl">{title}</h2>
        </div>
        <div className="text-luxury-body text-sm space-y-3">{children}</div>
      </section>
    </Reveal>
  );
}

const Row = ({ label, value }) => value ? (
  <div className="flex justify-between gap-4 border-b border-white/[0.05] pb-2">
    <span className="text-white/40">{label}</span><span className="text-white/80 text-right">{value}</span>
  </div>
) : null;

export default function RulesPage() {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | error

  useEffect(() => {
    api.get("/rules").then((r) => { setData(r.data); setState("ready"); }).catch(() => setState("error"));
  }, []);

  if (state === "loading") {
    return (
      <div className="pt-32 pb-24 min-h-screen" data-testid="rules-page">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-6" aria-busy="true">
          {[...Array(6)].map((_, i) => <div key={i} className="glass h-44 animate-pulse" />)}
        </div>
      </div>
    );
  }
  if (state === "error" || !data) {
    return (
      <div className="pt-40 pb-24 min-h-screen text-center" data-testid="rules-page">
        <CircleAlert className="mx-auto text-gold mb-4" />
        <p className="text-white/60">We couldn't load the house rules right now. Please try again shortly.</p>
      </div>
    );
  }

  const t = data.timings, r = data.rules, c = data.cancellation;
  return (
    <div className="pt-32 pb-24 grain min-h-screen" data-testid="rules-page">
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-14">
          <div className="eyebrow">Before You Arrive</div>
          <h1 className="font-display text-5xl sm:text-6xl mt-6">House Rules</h1>
          <div className="divider-gold mx-auto" />
          <p className="text-luxury-body max-w-xl mx-auto">Everything about check-in, check-out, cancellations and conduct at {data.name}, in one place.</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 mb-6">
          <div className="glass p-8 text-center border border-gold/20" data-testid="rules-checkin-time">
            <LogIn className="text-gold mx-auto mb-3" size={22} />
            <div className="eyebrow">Check-in</div>
            <div className="font-display text-4xl mt-3">From {t.check_in_label}</div>
          </div>
          <div className="glass p-8 text-center border border-gold/20" data-testid="rules-checkout-time">
            <LogOut className="text-gold mx-auto mb-3" size={22} />
            <div className="eyebrow">Check-out</div>
            <div className="font-display text-4xl mt-3">Before {t.check_out_label}</div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <Card icon={LogIn} title="Check-in rules" testid="rules-checkin">
            <Row label="Check-in time" value={`From ${t.check_in_label}`} />
            <Row label="Minimum age" value={r.minimum_age ? `${r.minimum_age}+` : ""} />
            {t.early_checkin_enabled && <Row label="Early check-in" value={`From ${t.early_checkin_label}${Number(t.early_checkin_fee) ? ` (+₹${Number(t.early_checkin_fee)})` : ""}`} />}
            {r.early_checkin_policy && <p>{r.early_checkin_policy}</p>}
            {r.late_checkin_policy && <p>{r.late_checkin_policy}</p>}
          </Card>

          <Card icon={LogOut} title="Check-out rules" testid="rules-checkout">
            <Row label="Check-out time" value={`Before ${t.check_out_label}`} />
            {t.late_checkout_enabled && <Row label="Late check-out" value={`Until ${t.late_checkout_label}${Number(t.late_checkout_fee) ? ` (+₹${Number(t.late_checkout_fee)})` : ""}`} />}
            {r.late_checkout_policy && <p>{r.late_checkout_policy}</p>}
            {r.guest_responsibilities && <p><span className="text-white/70">Your responsibilities: </span>{r.guest_responsibilities}</p>}
            {r.key_return_instructions && <p><span className="text-white/70">Keys: </span>{r.key_return_instructions}</p>}
            {r.property_condition_requirements && <p><span className="text-white/70">Condition: </span>{r.property_condition_requirements}</p>}
          </Card>

          <Card icon={IdCard} title="Guest requirements" testid="rules-guests">
            <p>{r.required_identification || "Valid government-issued identification may be required."}</p>
            <Row label="Maximum guests" value={String(data.max_guests)} />
            <Row label="Minimum age" value={r.minimum_age ? `${r.minimum_age}+` : ""} />
          </Card>

          <Card icon={KeyRound} title="Access" testid="rules-access">
            <p>{r.access_instructions || "Access details are shared after your booking is confirmed."}</p>
          </Card>

          <Card icon={ScrollText} title="Cancellation" testid="rules-cancellation">
            <div className="text-white/80">{c.name}</div>
            <ul className="list-disc pl-5 space-y-1">
              {c.lines.map((l) => <li key={l}>{l}</li>)}
            </ul>
          </Card>

          <Card icon={Ban} title="Non-refundable bookings" tone="crimson" testid="rules-non-refundable">
            <p>Bookings marked <span className="text-red-300">non-refundable</span> cannot be refunded after confirmation, subject to the property's applicable terms. You will be asked to acknowledge this before you can confirm such a booking.</p>
            {c.refund_type === "NON_REFUNDABLE" && <p className="text-red-300">Current rate at {data.name}: non-refundable.</p>}
          </Card>

          {r.property_rules.length > 0 && (
            <Card icon={ShieldAlert} title="Property rules" testid="rules-property">
              <ul className="list-disc pl-5 space-y-1">{r.property_rules.map((x) => <li key={x}>{x}</li>)}</ul>
            </Card>
          )}

          {r.important_information.length > 0 && (
            <Card icon={Info} title="Important information" testid="rules-important">
              <ul className="list-disc pl-5 space-y-1">{r.important_information.map((x) => <li key={x}>{x}</li>)}</ul>
            </Card>
          )}
        </div>

        <div className="text-center mt-14">
          <Link to="/booking" className="btn-gold inline-flex items-center gap-2"><Clock size={14} /> Check availability</Link>
          <p className="text-white/30 text-xs mt-4">See also our <Link className="text-gold" to="/policies">full policies</Link>.</p>
        </div>
      </div>
    </div>
  );
}
